import React, { useState, useEffect, useCallback } from 'react';
import './SessionPopupDialog.scss';

/**
 * SessionPopupDialog Component
 * Displays a modal dialog when session is about to expire
 * 
 * @param {Function} onContinue - Function to call when "Continue Session" is clicked
 * @param {Function} onLogout - Function to call when "Logout" is clicked
 * @param {number} timeoutDuration - Duration in ms before auto-logout happens
 */
const SessionPopupDialog = ({ onContinue, onLogout, timeoutDuration }) => {
  // Convert ms to seconds for countdown
  const [timeLeft, setTimeLeft] = useState(Math.floor(timeoutDuration / 1000));
  const [intervalId, setIntervalId] = useState(null);
  
  // Format time as MM:SS
  const formatTime = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);
  
  // Setup countdown timer when component mounts
  useEffect(() => {
    // Set up timer countdown
    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    setIntervalId(interval);
    
    // Cleanup timer when component unmounts
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, []);
  
  // Handle "Continue Session" button click
  const handleContinue = () => {
    if (intervalId) {
      clearInterval(intervalId);
    }
    onContinue();
  };
  
  // Handle "Logout" button click
  const handleLogout = () => {
    if (intervalId) {
      clearInterval(intervalId);
    }
    onLogout();
  };

  // Set the title and message for session timeout
  const title = 'Session Timeout';
  return (
    <div className="session-timeout-overlay">
      <div className="session-timeout-dialog">
        <div className="session-timeout-content">
          <h2>{title}</h2>
          <p className="session-timeout-message">
            Your session has reached its maximum duration. It will expire in <span className="session-timeout-countdown">{formatTime(timeLeft)}</span>.
            <br />
            Would you like to continue?
          </p>

          <div className="session-timeout-actions">
            <button
              className="session-btn-secondary"
              onClick={handleLogout}
            >
              Logout
            </button>
            <button
              className="session-btn-primary"
              onClick={handleContinue}
            >
              Continue Session
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionPopupDialog;
